import { MessageRequest, MessageResult, RecipientResult, MessageStatus } from '../types/message.types';

export interface Provider {
  id: string;
  name: string;
  send(request: ProviderMessageRequest): Promise<ProviderMessageResult>;
}

export interface ProviderMessageRequest {
  templateCode: string;
  phoneNumber: string;
  variables: Record<string, any>;
  options?: Record<string, any>;
}

export interface ProviderMessageResult {
  messageId: string;
  status: MessageStatus;
  error?: { code: string; message: string };
}

export class SingleMessageSender {
  private providers: Map<string, Provider> = new Map();
  private templates: Map<string, any> = new Map(); // Template cache

  addProvider(provider: Provider): void {
    this.providers.set(provider.id, provider);
  }

  removeProvider(providerId: string): void {
    this.providers.delete(providerId);
  }

  async send(request: MessageRequest): Promise<MessageResult> {
    const requestId = this.generateRequestId();
    const results: RecipientResult[] = [];

    // Get template information
    const template = await this.getTemplate(request.templateId);
    if (!template) {
      throw new Error(`Template ${request.templateId} not found`);
    }

    // Get provider
    const provider = this.providers.get(template.provider);
    if (!provider) {
      throw new Error(`Provider ${template.provider} not found`);
    }

    // Process each recipient
    for (const recipient of request.recipients) {
      try {
        const result = await this.sendToRecipient(
          provider,
          template,
          recipient,
          request.variables,
          request.options
        );
        results.push(result);
      } catch (error) {
        results.push({
          phoneNumber: recipient.phoneNumber,
          status: MessageStatus.FAILED,
          error: {
            code: 'SEND_ERROR',
            message: error instanceof Error ? error.message : 'Unknown error'
          },
          metadata: recipient.metadata
        });
      }
    }

    // Calculate summary
    const summary = this.calculateSummary(results);

    return {
      requestId,
      results,
      summary,
      metadata: {
        createdAt: new Date(),
        provider: template.provider,
        templateId: request.templateId
      }
    };
  }

  private async sendToRecipient(
    provider: Provider,
    template: any,
    recipient: any,
    commonVariables: Record<string, any>,
    options?: any
  ): Promise<RecipientResult> {
    // Merge common variables with recipient-specific variables
    const variables = { ...commonVariables, ...recipient.variables };

    // Note: Variable parsing moved to personalization system
    // Variables are now handled by VariableReplacer in the personalization package

    // Prepare provider request
    const providerRequest: ProviderMessageRequest = {
      templateCode: template.code,
      phoneNumber: recipient.phoneNumber,
      variables,
      options
    };

    // Send message
    const providerResult = await provider.send(providerRequest);

    return {
      phoneNumber: recipient.phoneNumber,
      messageId: providerResult.messageId,
      status: providerResult.status,
      error: providerResult.error,
      metadata: recipient.metadata
    };
  }

  private async getTemplate(templateId: string): Promise<any> {
    // Check cache first
    if (this.templates.has(templateId)) {
      return this.templates.get(templateId);
    }

    // In a real implementation, this would fetch from a database
    // For now, return a mock template
    const template = {
      id: templateId,
      code: 'TEMPLATE_CODE',
      provider: 'mock-provider',
      variables: [],
      content: 'Mock template content'
    };

    this.templates.set(templateId, template);
    return template;
  }

  private calculateSummary(results: RecipientResult[]) {
    return {
      total: results.length,
      queued: results.filter(r => r.status === MessageStatus.QUEUED).length,
      sent: results.filter(r => r.status === MessageStatus.SENT).length,
      failed: results.filter(r => r.status === MessageStatus.FAILED).length
    };
  }

  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  async cancelMessage(messageId: string): Promise<boolean> {
    // Implementation for canceling a scheduled message
    // This would interact with the queue system
    throw new Error('Not implemented');
  }

  async getMessageStatus(messageId: string): Promise<MessageStatus> {
    // Implementation for getting message status
    // This would query the delivery tracking system
    throw new Error('Not implemented');
  }

  async resendMessage(messageId: string, options?: { newRecipient?: string }): Promise<MessageResult> {
    // Implementation for resending a failed message
    throw new Error('Not implemented');
  }
}